import { useEffect, useState, useCallback, useRef } from 'react';
import websocketService from '../services/websocketService';
import notificationService from '../utils/notificationService';
import toast from 'react-hot-toast';

export const useWebSocket = (userId, options = {}) => {
  const {
    onMessage = null,
    onNotification = null,
    enabled = true,
    isChatMinimized = true,
    activeConversationUserId = null,
    onOpenChat = null
  } = options;

  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Trzymaj najnowsze opcje w ref, żeby nie resetować połączenia przy każdej zmianie referencji callbacków
  const optionsRef = useRef({
    onMessage,
    onNotification,
    isChatMinimized,
    activeConversationUserId,
    onOpenChat,
  });

  useEffect(() => {
    optionsRef.current = {
      onMessage,
      onNotification,
      isChatMinimized,
      activeConversationUserId,
      onOpenChat,
    };
  }, [onMessage, onNotification, isChatMinimized, activeConversationUserId, onOpenChat]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!enabled) {
      setConnected(false);
      return;
    }
    if (!token) {
      console.warn('⚠️ No token found, skipping WebSocket connection');
      return;
    }
    if (!userId) {
      console.warn('⚠️ No userId, skipping WebSocket connection');
      return;
    }

    let isMounted = true;

    // Połącz z WebSocket
    console.log('🔄 Attempting WebSocket connection...');
    websocketService.connect(token)
      .then(() => {
        if (isMounted) {
          console.log('✅ WebSocket connected in useWebSocket hook');
          setConnected(true);
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error('❌ WebSocket connection error in useWebSocket:', error);
        setConnected(false);
      });

    // Subskrypcja wiadomości
    const messageCallback = (message) => {
      setMessages(prev => [message, ...prev]);
      
      if (optionsRef.current.onMessage) {
        optionsRef.current.onMessage(message);
      }

      // Powiadomienia - wywołaj asynchronicznie, aby nie blokować WebSocket
      setTimeout(() => {
        const {
          isChatMinimized: minimized,
          activeConversationUserId: activeConversation,
          onOpenChat: openChat,
        } = optionsRef.current;
        notificationService.notifyNewMessage(message, {
          isChatMinimized: minimized,
          isActiveConversation: activeConversation === message.senderId,
          currentUserId: userId,
          onOpenChat: openChat ? () => {
            // Wywołaj onClick w kontekście event handlera użytkownika
            // Przekaż senderId, aby otworzyć konkretną konwersację
            if (openChat) {
              openChat(message.senderId);
            }
          } : null
        });
      }, 0);
    };

    websocketService.subscribeToMessages(messageCallback);

    // Subskrypcja powiadomień
    const notificationCallback = (notification) => {
      setNotifications(prev => [notification, ...prev]);
      
      // Powiadomienie o zwrocie faktury - pokaż toast i opcjonalnie przekieruj
      if (notification?.type === 'INVOICE_RETURNED' && notification?.message) {
        toast.success(notification.message, {
          duration: 6000,
          icon: '📄'
        });
      }
      
      if (onNotification) {
        optionsRef.current.onNotification(notification);
      }
    };

    websocketService.subscribeToNotifications(notificationCallback);

    // Cleanup przy unmount - usuń callbacki aby uniknąć duplikatów
    return () => {
      isMounted = false;
      websocketService.unsubscribeFromMessages(messageCallback);
      websocketService.unsubscribeFromNotifications(notificationCallback);
    };
  }, [userId, enabled]);

  const sendMessage = useCallback((recipientId, content) => {
    const isWsConnected = websocketService.isConnected();
    if (!connected && !isWsConnected) {
      console.error('WebSocket not connected. State:', { connected, isWsConnected });
      return false;
    }

    try {
      websocketService.sendMessage(recipientId, content);
      console.log('Message sent via WebSocket:', { recipientId, content });
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }, [connected]);

  return {
    connected,
    messages,
    notifications,
    sendMessage,
    isConnected: websocketService.isConnected()
  };
};

export default useWebSocket;
